import { useMemo, useState, useEffect, useCallback } from "react";
import { Table, TableBody } from "@/components/ui/table";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  SortingState,
} from "@tanstack/react-table";
import { SortableTableHeader } from "@/components/table/table-header";
import { SkeletonTableHeader } from "@/components/table/skeleton-table-header";
import { Pagination } from "@/components/table/pagination";
import { SkeletonTableBody } from "@/components/ui/skeleton-table-body";
import { DivergenceTableBody } from "./divergences-table-body";
import { columns } from "./columns";
import { DivergencesFilterToolbar } from "./divergences-filter-toolbar";
import { useTableDefaultPageSize } from "@/hooks/use-table-default-page-size";
import { useClampPageIndex } from "@/hooks/use-clamp-page-index";
import { usePersistedPageSize } from "@/hooks/use-persisted-page-size";
import type { DivergenceSearch } from "../route";

export interface DivergenceReport {
  id: number;
  username: string;
  userAgent: string;
  service: string;
  callId: string;
  caller: string;
  created: string;
  reason: string;
  message: string;
}

type DivergenceSearchUpdate = Partial<DivergenceSearch> & { replace?: boolean };

export interface DivergenceReportsTableProps {
  divergenceReports: DivergenceReport[];
  isLoading: boolean;
  defaultDivergenceId?: number;
  defaultSearch?: string;
  defaultPage?: number;
  defaultPageSize?: number;
  after?: string;
  before?: string;
  maxDateRange: number;
  initialAfter?: string;
  initialBefore?: string;
  syncSearch: (updates: DivergenceSearchUpdate) => void;
  resetFilters: () => void;
  clearDateRange: () => void;
  setAfter: (after: string) => void;
  setBefore: (before: string) => void;
}

export function DivergenceReportsTable({
  divergenceReports,
  isLoading,
  defaultDivergenceId,
  defaultSearch = "",
  setAfter,
  setBefore,
  after,
  before,
  defaultPage,
  defaultPageSize,
  maxDateRange,
  initialAfter,
  initialBefore,
  syncSearch,
  resetFilters,
  clearDateRange,
}: DivergenceReportsTableProps) {
  const screenDefaultPageSize = useTableDefaultPageSize();
  const {
    storedPageSize,
    persist: persistPageSize,
    clear: clearPageSizePreference,
  } = usePersistedPageSize("divergences");

  const [search, setSearch] = useState<string | number | undefined>(defaultSearch);

  const pageSize = defaultPageSize ?? storedPageSize ?? screenDefaultPageSize;

  const [pagination, setPagination] = useState({
    pageIndex: defaultPage && defaultPage > 0 ? defaultPage - 1 : 0,
    pageSize,
  });


  useEffect(() => {
    setSearch(defaultSearch);
  }, [defaultSearch]);

  useEffect(() => {
    // Use provided defaultPageSize from URL params if available, otherwise use stored or screen default
    setPagination({
      pageIndex: defaultPage && defaultPage > 0 ? defaultPage - 1 : 0,
      pageSize: defaultPageSize ?? storedPageSize ?? screenDefaultPageSize,
    });
  }, [defaultPage, defaultPageSize, storedPageSize, screenDefaultPageSize]);

  useEffect(() => {
    if (!after?.length || !before?.length) return;

    const currentPage = pagination.pageIndex + 1;
    const shouldIncludePage = currentPage > 1;
    const shouldIncludePageSize = pagination.pageSize !== screenDefaultPageSize;

    const urlPage = shouldIncludePage ? currentPage : undefined;
    const urlPageSize = shouldIncludePageSize ? pagination.pageSize : undefined;

    const needsUpdate = defaultPage !== urlPage || defaultPageSize !== urlPageSize;

    if (needsUpdate) {
      syncSearch({ page: urlPage, pageSize: urlPageSize });
    }
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    after,
    before,
    defaultPage,
    defaultPageSize,
    syncSearch,
    screenDefaultPageSize,
  ]);

  const filteredReports = useMemo(() => {
    let data = divergenceReports;

    if (search && search.toString().trim()) {
      const lower = search.toString().toLowerCase();
      data = data.filter(
        (r) =>
          r.username.toLowerCase().includes(lower) ||
          r.userAgent.toLowerCase().includes(lower) ||
          r.service.toLowerCase().includes(lower) ||
          r.callId.toString().toLowerCase().includes(lower) ||
          r.caller.toLowerCase().includes(lower) ||
          r.reason.toLowerCase().includes(lower) ||
          r.message.toLowerCase().includes(lower)
      );
    }

    return data;
  }, [divergenceReports, search]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const table = useReactTable({
    data: filteredReports,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    autoResetPageIndex: false,
    manualPagination: false,
  });

  const handleRowClick = (id: number) => {
    const report = divergenceReports.find((r) => r.id === id);
    if (!report) return;

    const currentPage = pagination.pageIndex + 1;
    const shouldIncludePage = currentPage > 1;
    const shouldIncludePageSize = pagination.pageSize !== screenDefaultPageSize;

    syncSearch({
      divergenceId: id,
      search: (search && search.toString()) || undefined,
      after,
      before,
      page: shouldIncludePage ? currentPage : undefined,
      pageSize: shouldIncludePageSize ? pagination.pageSize : undefined,
    });
  };

  const onClamp = useCallback(
    (newPageIndex: number) => {
      const newPage = newPageIndex + 1;
      syncSearch({
        page: newPage > 1 ? newPage : undefined,
      });
    },
    [syncSearch]
  );

  useClampPageIndex({
    pageIndex: pagination.pageIndex,
    pageCount: table?.getPageCount?.() ?? 0,
    onClamp,
  });

  const handleFilterChange = (filters: { search?: string; from?: string; to?: string }) => {
    const datesChanged = filters.from !== undefined || filters.to !== undefined;
    const searchChanged = filters.search !== undefined;

    if (!datesChanged && !searchChanged) return;

    const newSearch = filters.search ?? search?.toString() ?? "";
    const newAfter = filters.from;
    const newBefore = filters.to;
    const resolvedAfter = newAfter ?? after;
    const resolvedBefore = newBefore ?? before;

    if (searchChanged) {
      setSearch(newSearch);
    }
    if (newAfter !== undefined) setAfter(newAfter);
    if (newBefore !== undefined) setBefore(newBefore);

    if (
      (searchChanged && newSearch !== (defaultSearch ?? "")) ||
      (newAfter !== undefined && newAfter !== after) ||
      (newBefore !== undefined && newBefore !== before)
    ) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }

    if (!resolvedAfter?.length || !resolvedBefore?.length) return;

    const shouldIncludePageSize = pagination.pageSize !== screenDefaultPageSize;

    syncSearch({
      ...(searchChanged ? { search: newSearch || undefined } : {}),
      after: resolvedAfter,
      before: resolvedBefore,
      page: undefined,
      pageSize: shouldIncludePageSize ? pagination.pageSize : undefined,
    });
  };

  return (
    <div>
      <DivergencesFilterToolbar
        onFilterChange={handleFilterChange}
        onClearDateRange={() => {
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          clearDateRange();
        }}
        onResetFilters={() => {
          setSearch("");
          setPagination({ pageIndex: 0, pageSize: screenDefaultPageSize });
          resetFilters();
        }}
        defaultSearch={(search && search.toString()) || undefined}
        defaultFrom={after}
        defaultTo={before}
        initialFrom={initialAfter}
        initialTo={initialBefore}
        maxDateRange={maxDateRange}
      />
      <Table className="border border-gray-300">
        {isLoading ? <SkeletonTableHeader table={table} /> : <SortableTableHeader table={table} />}
        <TableBody>
          {isLoading ? (
            <SkeletonTableBody pageSize={10} table={table} />
          ) : (
            <DivergenceTableBody table={table} onRowClick={handleRowClick} selectedId={defaultDivergenceId} />
          )}
        </TableBody>
      </Table>
      <Pagination table={table} onDefaultReset={clearPageSizePreference} onPageSizeChange={persistPageSize} />
    </div>
  );
}
